import React, { useState, useEffect } from 'react';
import { portalIntegrationApi } from '../utils/portalIntegrationApi';

const PORTAL_STATUS = {
  NOT_CONNECTED: { label: 'Nicht angebunden', color: 'bg-gray-100 text-gray-700', icon: '❌' },
  DATA_SUBMITTED: { label: 'Daten übermittelt', color: 'bg-yellow-100 text-yellow-700', icon: '🟡' },
  ACTIVE: { label: 'Aktiv angebunden', color: 'bg-green-100 text-[var(--success-text)]', icon: '🟢' },
};

function Lieferportale() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [formData, setFormData] = useState({
    restaurant_name: '',
    lieferando_customer_number: '',
    company_name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    contact_name: '',
    email: '',
    phone: '',
    lieferando_link: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadPortals();
  }, []);

  const loadPortals = async () => {
    try {
      setLoading(true);
      const data = await portalIntegrationApi.getAll();
      setPortals(data || []);
    } catch (err) {
      console.error('Error loading portals:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (portal) => {
    setSelectedPortal(portal);
    setShowModal(true);
    setFormErrors({});
    setMessage(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPortal(null);
    setFormData({
      restaurant_name: '',
      lieferando_customer_number: '',
      company_name: '',
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      country: 'Deutschland',
      contact_name: '',
      email: '',
      phone: '',
      lieferando_link: '',
      notes: '',
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    const required = [
      'restaurant_name',
      'company_name',
      'street',
      'house_number',
      'postal_code',
      'city',
      'contact_name',
      'email',
      'phone',
    ];

    required.forEach((field) => {
      if (!formData[field] || formData[field].trim() === '') {
        errors[field] = 'Pflichtfeld';
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }

    // Phone validation
    if (formData.phone && !/^\+?[0-9\s\-()]+$/.test(formData.phone)) {
      errors.phone = 'Ungültige Telefonnummer';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Bitte alle Pflichtfelder ausfüllen' });
      return;
    }

    try {
      setLoading(true);
      await portalIntegrationApi.submitLieferandoData(formData);
      
      setMessage({
        type: 'success',
        text: 'Daten erfolgreich an Lieferando übermittelt! Die Einrichtung erfolgt durch Lieferando.',
      });
      
      setTimeout(() => {
        closeModal();
        loadPortals();
      }, 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Übermitteln: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: null });
    }
  };

  const lieferandoPortal = portals.find((p) => p.name === 'lieferando') || {
    name: 'lieferando',
    display_name: 'Lieferando',
    status: 'not_connected',
    logo: '🍕',
  };

  const statusConfig = PORTAL_STATUS[lieferandoPortal.status?.toUpperCase()] || PORTAL_STATUS.NOT_CONNECTED;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Lieferportale
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">Verwalten Sie Ihre Anbindungen an Lieferportale</p>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200'
              : 'bg-[var(--error-bg)] text-[var(--error-text)] border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Portal Cards */}
      <div className="grid gap-4">
        {/* Lieferando */}
        <div
          className="dark-card rounded-lg shadow border p-6 hover:shadow-lg transition-shadow"
          data-testid="portal-card-lieferando"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-5xl">{lieferandoPortal.logo}</div>
              <div>
                <h3 className="text-2xl font-bold">{lieferandoPortal.display_name}</h3>
                <div className="mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                    {statusConfig.icon} {statusConfig.label}
                  </span>
                </div>
                {lieferandoPortal.submitted_at && (
                  <div className="text-xs text-[var(--text-secondary)] mt-2">
                    Daten übermittelt am: {new Date(lieferandoPortal.submitted_at).toLocaleString('de-DE')}
                  </div>
                )}
              </div>
            </div>

            <div>
              {lieferandoPortal.status === 'not_connected' && (
                <button
                  onClick={() => openModal(lieferandoPortal)}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition-colors"
                  data-testid="connect-lieferando-button"
                >
                  Lieferando anbinden
                </button>
              )}
              {lieferandoPortal.status === 'data_submitted' && (
                <button
                  className="px-6 py-3 bg-yellow-600 text-white rounded-lg font-bold cursor-not-allowed"
                  disabled
                >
                  Wartet auf Lieferando
                </button>
              )}
              {lieferandoPortal.status === 'active' && (
                <button className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors">
                  Status anzeigen
                </button>
              )}
            </div>
          </div>

          {lieferandoPortal.status === 'data_submitted' && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              <strong>Nächster Schritt:</strong> Lieferando richtet die Schnittstelle ein. Sie werden benachrichtigt,
              sobald die Anbindung aktiv ist.
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="dark-card rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">🍕 Lieferando anbinden</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-[var(--text-secondary)] text-2xl">
                ×
              </button>
            </div>

            <div className="mb-6 p-4 bg-[var(--accent-muted)] border border-blue-200 rounded-lg text-sm text-blue-800">
              <strong>Hinweis:</strong> Wir übermitteln diese Daten an Lieferando, damit die technische Anbindung
              an unser Kassensystem eingerichtet wird.
            </div>

            {message && (
              <div
                className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200'
                    : 'bg-[var(--error-bg)] text-[var(--error-text)] border border-red-200'
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Restaurant Info */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 text-gray-800">Restaurant-Informationen</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Restaurantname <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.restaurant_name}
                      onChange={(e) => handleInputChange('restaurant_name', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.restaurant_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="exakt wie bei Lieferando"
                      data-testid="restaurant-name-input"
                    />
                    {formErrors.restaurant_name && (
                      <span className="text-xs text-red-500">{formErrors.restaurant_name}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Lieferando Kundennummer / Store-ID</label>
                    <input
                      type="text"
                      value={formData.lieferando_customer_number}
                      onChange={(e) => handleInputChange('lieferando_customer_number', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="falls vorhanden"
                      data-testid="customer-number-input"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">
                    Firmenname (rechtlich) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      formErrors.company_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="GmbH, UG, etc."
                    data-testid="company-name-input"
                  />
                  {formErrors.company_name && <span className="text-xs text-red-500">{formErrors.company_name}</span>}
                </div>
              </div>

              {/* Address */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 text-gray-800">Adresse</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Straße <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.street}
                      onChange={(e) => handleInputChange('street', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.street ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="street-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Hausnr. <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.house_number}
                      onChange={(e) => handleInputChange('house_number', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.house_number ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="house-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      PLZ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.postal_code ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Ort <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Land</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      data-testid="country-input"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 text-gray-800">Ansprechpartner</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange('contact_name', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        formErrors.contact_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        E-Mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          formErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        data-testid="email-input"
                      />
                      {formErrors.email && <span className="text-xs text-red-500">{formErrors.email}</span>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg ${
                          formErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        data-testid="phone-input"
                      />
                      {formErrors.phone && <span className="text-xs text-red-500">{formErrors.phone}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Optional */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 text-gray-800">Optional</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Link zum Lieferando-Restaurant</label>
                  <input
                    type="url"
                    value={formData.lieferando_link}
                    onChange={(e) => handleInputChange('lieferando_link', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="https://www.lieferando.de/..."
                    data-testid="lieferando-link-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Hinweise</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                    placeholder="z.B. mehrere Stores, gewünschtes Go-Live-Datum..."
                    data-testid="notes-input"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {loading ? 'Wird übermittelt...' : 'Daten an Lieferando übermitteln'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lieferportale;
